/**
 * 메뉴 권한 설정 페이지 컴포넌트
 * 
 * 제휴사 Admin 소스를 본사에 맞게 적용
 * 메뉴별 권한 설정 (롤별, 개별 관리자별)
 * 
 * 작성일: 2025-12-01
 */

import React, { useState } from 'react';
import { Card, Table, Form, Button, Badge, Modal, Tabs, Tab, Row, Col } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Navigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import { getMenus } from '../../services/menuService';
import { adminService } from '../../services/adminService';
import { getMenuPermissions, setMenuPermission, deleteMenuPermission } from '../../services/menuPermissionService';
import type { MenuPermission } from '../../types/menuPermission';
import type { Menu } from '../../types/menu';
import type { AdminUser } from '../../types/admin';
import { useAuth } from '../../contexts/AuthContext';
import './MenuPermissions.css';

const MenuPermissions: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user: currentAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState<'role' | 'admin'>('role');
  const [selectedMenuId, setSelectedMenuId] = useState<number | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>('super_admin');
  const [selectedAdminId, setSelectedAdminId] = useState<number | null>(null);
  const [permissions, setPermissions] = useState({
    can_read: false,
    can_create: false,
    can_update: false,
    can_delete: false,
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [permissionToDelete, setPermissionToDelete] = useState<MenuPermission | null>(null);

  // 메뉴 목록 조회
  const { data: menusData } = useQuery({
    queryKey: ['menus'],
    queryFn: () => getMenus({ is_active: undefined }),
  });

  // 관리자 목록 조회
  const { data: adminsData } = useQuery({
    queryKey: ['admins', 'for-permissions'],
    queryFn: () => adminService.getAdmins({ limit: 1000 }),
    enabled: activeTab === 'admin',
  });

  // 권한 목록 조회
  const { data: permissionsData } = useQuery({
    queryKey: ['menu-permissions', selectedMenuId, selectedRole, selectedAdminId],
    queryFn: () =>
      getMenuPermissions({
        menu_id: selectedMenuId || undefined,
        role: activeTab === 'role' ? selectedRole : undefined,
        admin_id: activeTab === 'admin' ? selectedAdminId || undefined : undefined,
      }),
    enabled: !!selectedMenuId,
  });

  const setPermissionMutation = useMutation({
    mutationFn: (data: {
      menu_id: number;
      role?: string | null;
      admin_id?: number | null;
      can_read: boolean;
      can_create: boolean;
      can_update: boolean;
      can_delete: boolean;
    }) => setMenuPermission(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu-permissions'] });
      queryClient.invalidateQueries({ queryKey: ['menus'] }); // 사이드바 메뉴 갱신
      alert('권한이 설정되었습니다.');
    },
    onError: (error: any) => {
      alert(`권한 설정 실패: ${error.response?.data?.error?.message || error.message}`);
    },
  });

  const deletePermissionMutation = useMutation({
    mutationFn: (id: number) => deleteMenuPermission(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu-permissions'] });
      setShowDeleteModal(false);
      setPermissionToDelete(null);
      alert('권한이 삭제되었습니다.');
    },
    onError: (error: any) => {
      alert(`권한 삭제 실패: ${error.response?.data?.error?.message || error.message}`);
    },
  });

  /**
   * 메뉴 선택 핸들러
   */
  const handleMenuSelect = (menuId: number) => {
    setSelectedMenuId(menuId);
    // 기존 권한 조회 및 설정
    const existingPermission = permissionsData?.data?.permissions?.find(
      (p: MenuPermission) =>
        p.menu_id === menuId &&
        (activeTab === 'role' ? p.role === selectedRole : p.admin_id === selectedAdminId)
    );

    if (existingPermission) {
      setPermissions({
        can_read: existingPermission.can_read,
        can_create: existingPermission.can_create,
        can_update: existingPermission.can_update,
        can_delete: existingPermission.can_delete,
      });
    } else {
      setPermissions({
        can_read: false,
        can_create: false,
        can_update: false,
        can_delete: false,
      });
    }
  };

  /**
   * 롤 선택 핸들러
   */
  const handleRoleSelect = (role: string) => {
    setSelectedRole(role);
    // 기존 권한 조회 및 설정
    if (selectedMenuId) {
      const existingPermission = permissionsData?.data?.permissions?.find(
        (p: MenuPermission) => p.menu_id === selectedMenuId && p.role === role
      );

      if (existingPermission) {
        setPermissions({
          can_read: existingPermission.can_read,
          can_create: existingPermission.can_create,
          can_update: existingPermission.can_update,
          can_delete: existingPermission.can_delete,
        });
      } else {
        setPermissions({
          can_read: false,
          can_create: false,
          can_update: false,
          can_delete: false,
        });
      }
    }
  };

  /**
   * 관리자 선택 핸들러
   */
  const handleAdminSelect = (adminId: number) => {
    setSelectedAdminId(adminId);
    // 기존 권한 조회 및 설정
    if (selectedMenuId) {
      const existingPermission = permissionsData?.data?.permissions?.find(
        (p: MenuPermission) => p.menu_id === selectedMenuId && p.admin_id === adminId
      );

      if (existingPermission) {
        setPermissions({
          can_read: existingPermission.can_read,
          can_create: existingPermission.can_create,
          can_update: existingPermission.can_update,
          can_delete: existingPermission.can_delete,
        });
      } else {
        setPermissions({
          can_read: false,
          can_create: false,
          can_update: false,
          can_delete: false,
        });
      }
    }
  };

  /**
   * 권한 저장 핸들러
   */
  const handleSave = () => {
    if (!selectedMenuId) {
      alert('메뉴를 선택해주세요.');
      return;
    }

    if (activeTab === 'role' && !selectedRole) {
      alert('권한 레벨을 선택해주세요.');
      return;
    }

    if (activeTab === 'admin' && !selectedAdminId) {
      alert('관리자를 선택해주세요.');
      return;
    }

    setPermissionMutation.mutate({
      menu_id: selectedMenuId,
      role: activeTab === 'role' ? selectedRole : null,
      admin_id: activeTab === 'admin' ? selectedAdminId : null,
      ...permissions,
    });
  };

  /**
   * 권한 삭제 핸들러
   */
  const handleDeleteClick = (permission: MenuPermission) => {
    setPermissionToDelete(permission);
    setShowDeleteModal(true);
  };

  /**
   * 권한 삭제 확인
   */
  const handleDeleteConfirm = () => {
    if (permissionToDelete) {
      deletePermissionMutation.mutate(permissionToDelete.id);
    }
  };

  /**
   * 권한 레벨 한글 표시
   */
  const getRoleLabel = (role: string): string => {
    switch (role) {
      case 'super_admin':
        return '최고 관리자';
      case 'admin':
        return '일반 관리자';
      case 'operator':
        return '운영자';
      default:
        return role;
    }
  };

  const menus: Menu[] = menusData?.data?.menus || [];
  const admins: AdminUser[] = adminsData?.admins || [];
  const permissionList: MenuPermission[] = permissionsData?.data?.permissions || [];

  // 권한 확인 (본사는 role 사용)
  if (currentAdmin?.role !== 'super_admin') {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return (
    <AdminLayout>
      <div>
        <div className="page-header">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1>메뉴 권한 설정</h1>
              <p>메뉴별 권한을 설정합니다. (롤별 또는 개별 관리자별)</p>
            </div>
            <Button variant="secondary" onClick={() => navigate('/admin/admins')}>
              관리자 목록
            </Button>
          </div>
        </div>

        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k as 'role' | 'admin')} className="mb-4">
          <Tab eventKey="role" title="롤별 권한 설정">
            <Card style={{ backgroundColor: '#3A4B5C', border: 'none' }}>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label style={{ color: '#fff' }}>메뉴 선택</Form.Label>
                      <Form.Select
                        value={selectedMenuId || ''}
                        onChange={(e) => handleMenuSelect(Number(e.target.value))}
                        style={{ backgroundColor: '#2E2E3A', color: '#fff', border: '1px solid #3A4B5C' }}
                      >
                        <option value="">메뉴를 선택하세요</option>
                        {menus.map((menu) => (
                          <option key={menu.id} value={menu.id}>
                            {menu.label} ({menu.path})
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label style={{ color: '#fff' }}>권한 레벨</Form.Label>
                      <Form.Select
                        value={selectedRole}
                        onChange={(e) => handleRoleSelect(e.target.value)}
                        style={{ backgroundColor: '#2E2E3A', color: '#fff', border: '1px solid #3A4B5C' }}
                      >
                        <option value="super_admin">최고 관리자</option>
                        <option value="admin">일반 관리자</option>
                        <option value="operator">운영자</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                {selectedMenuId && (
                  <>
                    <Card className="mb-3" style={{ backgroundColor: '#2E2E3A', border: 'none' }}>
                      <Card.Header style={{ backgroundColor: '#3A4B5C', color: '#fff', border: 'none' }}>
                        권한 설정
                      </Card.Header>
                      <Card.Body>
                        <Form.Check
                          type="checkbox"
                          label="조회 (Read)"
                          checked={permissions.can_read}
                          onChange={(e) =>
                            setPermissions((prev) => ({ ...prev, can_read: e.target.checked }))
                          }
                          className="mb-2"
                          style={{ color: '#fff' }}
                        />
                        <Form.Check
                          type="checkbox"
                          label="생성 (Create)"
                          checked={permissions.can_create}
                          onChange={(e) =>
                            setPermissions((prev) => ({ ...prev, can_create: e.target.checked }))
                          }
                          className="mb-2"
                          style={{ color: '#fff' }}
                        />
                        <Form.Check
                          type="checkbox"
                          label="수정 (Update)"
                          checked={permissions.can_update}
                          onChange={(e) =>
                            setPermissions((prev) => ({ ...prev, can_update: e.target.checked }))
                          }
                          className="mb-2"
                          style={{ color: '#fff' }}
                        />
                        <Form.Check
                          type="checkbox"
                          label="삭제 (Delete)"
                          checked={permissions.can_delete}
                          onChange={(e) =>
                            setPermissions((prev) => ({ ...prev, can_delete: e.target.checked }))
                          }
                          className="mb-2"
                          style={{ color: '#fff' }}
                        />
                        <Button
                          variant="primary"
                          onClick={handleSave}
                          disabled={setPermissionMutation.isPending}
                          className="mt-3"
                        >
                          {setPermissionMutation.isPending ? '저장 중...' : '권한 저장'}
                        </Button>
                      </Card.Body>
                    </Card>
                  </>
                )}
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="admin" title="개별 관리자 권한 설정">
            <Card style={{ backgroundColor: '#3A4B5C', border: 'none' }}>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label style={{ color: '#fff' }}>메뉴 선택</Form.Label>
                      <Form.Select
                        value={selectedMenuId || ''}
                        onChange={(e) => handleMenuSelect(Number(e.target.value))}
                        style={{ backgroundColor: '#2E2E3A', color: '#fff', border: '1px solid #3A4B5C' }}
                      >
                        <option value="">메뉴를 선택하세요</option>
                        {menus.map((menu) => (
                          <option key={menu.id} value={menu.id}>
                            {menu.label} ({menu.path})
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label style={{ color: '#fff' }}>관리자 선택</Form.Label>
                      <Form.Select
                        value={selectedAdminId || ''}
                        onChange={(e) => handleAdminSelect(Number(e.target.value))}
                        style={{ backgroundColor: '#2E2E3A', color: '#fff', border: '1px solid #3A4B5C' }}
                      >
                        <option value="">관리자를 선택하세요</option>
                        {admins.map((admin) => (
                          <option key={admin.id} value={admin.id}>
                            {admin.name} ({admin.email}) - {admin.role || '권한 없음'}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                {selectedMenuId && selectedAdminId && (
                  <>
                    <Card className="mb-3" style={{ backgroundColor: '#2E2E3A', border: 'none' }}>
                      <Card.Header style={{ backgroundColor: '#3A4B5C', color: '#fff', border: 'none' }}>
                        권한 설정
                      </Card.Header>
                      <Card.Body>
                        <Form.Check
                          type="checkbox"
                          label="조회 (Read)"
                          checked={permissions.can_read}
                          onChange={(e) =>
                            setPermissions((prev) => ({ ...prev, can_read: e.target.checked }))
                          }
                          className="mb-2"
                          style={{ color: '#fff' }}
                        />
                        <Form.Check
                          type="checkbox"
                          label="생성 (Create)"
                          checked={permissions.can_create}
                          onChange={(e) =>
                            setPermissions((prev) => ({ ...prev, can_create: e.target.checked }))
                          }
                          className="mb-2"
                          style={{ color: '#fff' }}
                        />
                        <Form.Check
                          type="checkbox"
                          label="수정 (Update)"
                          checked={permissions.can_update}
                          onChange={(e) =>
                            setPermissions((prev) => ({ ...prev, can_update: e.target.checked }))
                          }
                          className="mb-2"
                          style={{ color: '#fff' }}
                        />
                        <Form.Check
                          type="checkbox"
                          label="삭제 (Delete)"
                          checked={permissions.can_delete}
                          onChange={(e) =>
                            setPermissions((prev) => ({ ...prev, can_delete: e.target.checked }))
                          }
                          className="mb-2"
                          style={{ color: '#fff' }}
                        />
                        <Button
                          variant="primary"
                          onClick={handleSave}
                          disabled={setPermissionMutation.isPending}
                          className="mt-3"
                        >
                          {setPermissionMutation.isPending ? '저장 중...' : '권한 저장'}
                        </Button>
                      </Card.Body>
                    </Card>
                  </>
                )}
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>

        {/* 권한 목록 */}
        {selectedMenuId && (
          <Card style={{ backgroundColor: '#3A4B5C', border: 'none' }}>
            <Card.Header style={{ backgroundColor: '#2E2E3A', color: '#fff', border: 'none' }}>
              <h5 className="mb-0" style={{ color: '#fff' }}>설정된 권한 목록</h5>
            </Card.Header>
            <Card.Body>
              {permissionList.length === 0 ? (
                <p style={{ color: '#ccc' }}>설정된 권한이 없습니다.</p>
              ) : (
                <Table hover variant="dark">
                  <thead>
                    <tr>
                      <th>타입</th>
                      <th>대상</th>
                      <th>조회</th>
                      <th>생성</th>
                      <th>수정</th>
                      <th>삭제</th>
                      <th>관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {permissionList.map((permission) => (
                      <tr key={permission.id}>
                        <td>
                          <Badge bg={permission.role ? 'info' : 'warning'}>
                            {permission.role ? '롤' : '개별'}
                          </Badge>
                        </td>
                        <td>
                          <span style={{ color: '#fff' }}>
                            {permission.role ? getRoleLabel(permission.role) : `관리자 ID: ${permission.admin_id}`}
                          </span>
                        </td>
                        <td>
                          <Badge bg={permission.can_read ? 'success' : 'secondary'}>
                            {permission.can_read ? 'O' : 'X'}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg={permission.can_create ? 'success' : 'secondary'}>
                            {permission.can_create ? 'O' : 'X'}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg={permission.can_update ? 'success' : 'secondary'}>
                            {permission.can_update ? 'O' : 'X'}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg={permission.can_delete ? 'success' : 'secondary'}>
                            {permission.can_delete ? 'O' : 'X'}
                          </Badge>
                        </td>
                        <td>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteClick(permission)}
                          >
                            삭제
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        )}

        {/* 삭제 확인 모달 */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>권한 삭제 확인</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            정말로 이 권한을 삭제하시겠습니까?
            <br />
            <strong>
              {permissionToDelete?.role
                ? `${getRoleLabel(permissionToDelete.role)} - ${permissionToDelete.menu_label}`
                : `관리자 ID: ${permissionToDelete?.admin_id} - ${permissionToDelete?.menu_label}`}
            </strong>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              취소
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteConfirm}
              disabled={deletePermissionMutation.isPending}
            >
              {deletePermissionMutation.isPending ? '삭제 중...' : '삭제'}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </AdminLayout>
  );
};

export default MenuPermissions;
